import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getDate } from '@einhaus/meteoric-helpers';
import type { AssetOption, Entity, EntityObjectCompressed, EntityOptionObjectCompressed } from '@einhaus/project314-types';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { Card, EmptyState } from '@/components/UI';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useFetchEntities } from '@/hooks/useFetchEntities';
import { useFetchOptions } from '@/hooks/useFetchOptions';
import './PortfolioPerformance.tailwind.css';
import DailyLedger from './components/DailyLedger';
import DayDetailDrawer from './components/DayDetailDrawer';
import EquityDrawdownChart from './components/EquityDrawdownChart';
import PerformanceBreakdownTabs from './components/PerformanceBreakdownTabs';
import PerformanceControls from './components/PerformanceControls';
import PerformanceKpiTiles from './components/PerformanceKpiTiles';
import PnlCalendar from './components/PnlCalendar';
import StrategyAttributionTable from './components/StrategyAttributionTable';
import type { PortfolioStrategyExpectation } from './performanceTypes';
import {
    assignStrategyColors,
    buildEquityCurve,
    buildLedgerRows,
    buildStrategyRows,
    buildStrategyScopeItems,
    computeEquityStats,
    computeTradeStats,
    filterDaysByPeriod,
    formatShortDate,
    getDayDetail,
    getMonthKey,
    getPeriodStartDate,
    isBreakdownTab,
    isCalendarMode,
    isClosedTrade,
    isIsoDate,
    isPerformancePeriodKey,
    parseStrategyScopeParam,
    PERFORMANCE_PERIODS,
    scopeFromIds,
    tradeMatchesScope,
    type BreakdownTab,
    type CalendarMode,
    type PerformanceBasis,
    type PerformancePeriodKey
} from './performanceUtils';

const DEFAULT_PERIOD: PerformancePeriodKey = '1M';
const DEFAULT_LINE_COLOR = '#2962ff';

const PortfolioPerformance: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const portfolioId = id ? parseInt(id, 10) : 0;
    const [searchParams, setSearchParams] = useSearchParams();
    const today = useMemo(() => getDate({ format: 'ymd', timezone: 'America/New_York' }), []);

    // Shareable view state lives in the URL; presentation toggles stay local.
    const periodParam = searchParams.get('period');
    const period: PerformancePeriodKey = isPerformancePeriodKey(periodParam) ? periodParam : DEFAULT_PERIOD;
    const strategyParam = searchParams.get('strategy');
    const dayParam = searchParams.get('day');
    const selectedDate = isIsoDate(dayParam) ? dayParam : null;
    const monthParam = searchParams.get('month');
    const calendarMonthKey = monthParam && /^\d{4}-\d{2}$/.test(monthParam) ? monthParam : getMonthKey(today);
    const calendarModeParam = searchParams.get('calendar');
    const calendarMode: CalendarMode = isCalendarMode(calendarModeParam) ? calendarModeParam : 'equity';
    const tabParam = searchParams.get('tab');
    const breakdownTab: BreakdownTab = isBreakdownTab(tabParam) ? tabParam : 'month';

    const [basis, setBasis] = useState<PerformanceBasis>('trading');
    const [hideWeekends, setHideWeekends] = useState(false);
    const [hideFlatDays, setHideFlatDays] = useState(true);
    const [showDormantStrategies, setShowDormantStrategies] = useState(false);
    const [compareToBacktest, setCompareToBacktest] = useState(false);

    const updateParams = useCallback(
        (patch: Record<string, string | null>) => {
            setSearchParams(
                (previous) => {
                    const next = new URLSearchParams(previous);

                    for (const [key, value] of Object.entries(patch)) {
                        if (value === null) next.delete(key);
                        else next.set(key, value);
                    }

                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const portfolioQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolio.queryOptions({ id: portfolioId }),
            enabled: portfolioId > 0
        },
        { fallbackErrorMessage: 'Failed to load portfolio details', showErrorToast: true }
    );

    const breakdownQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioPerformanceBreakdown.queryOptions({ id: portfolioId }),
            enabled: portfolioId > 0,
            staleTime: 60_000
        },
        { fallbackErrorMessage: 'Failed to load performance data', showErrorToast: true }
    );

    const expectationsQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioStrategyExpectations.queryOptions({ portfolioId }),
            enabled: portfolioId > 0 && compareToBacktest,
            staleTime: 5 * 60_000
        },
        { fallbackErrorMessage: 'Failed to load baseline backtest expectations', showErrorToast: true }
    );

    const portfolio = portfolioQuery.data?.portfolios?.[0] ?? null;
    const breakdown = breakdownQuery.data ?? null;
    const days = useMemo(() => breakdown?.days ?? [], [breakdown]);
    const trades = useMemo(() => breakdown?.trades ?? [], [breakdown]);
    const strategies = useMemo(() => breakdown?.strategies ?? [], [breakdown]);

    const entityObjects = useMemo<EntityObjectCompressed[]>(() => {
        const seen = new Map<string, EntityObjectCompressed>();

        for (const trade of trades) {
            const key = `${trade.entityId}_${trade.entityType}`;
            if (seen.has(key)) continue;
            seen.set(key, { i: trade.entityId, t: trade.entityType === 'Asset' ? 'a' : trade.entityType === 'Cryptocurrency' ? 'c' : 'i' });
        }

        return [...seen.values()];
    }, [trades]);

    const optionObjects = useMemo<EntityOptionObjectCompressed[]>(() => {
        const seen = new Map<string, EntityOptionObjectCompressed>();

        for (const trade of trades) {
            if (trade.derivativeType !== 'Option' || trade.derivativeId === null) continue;
            if (trade.entityType !== 'Asset' && trade.entityType !== 'AssetIndex') continue;
            seen.set(`${trade.entityType}:${trade.entityId}:${trade.derivativeId}`, {
                oId: trade.derivativeId,
                eId: trade.entityId,
                eType: trade.entityType
            });
        }

        return [...seen.values()];
    }, [trades]);

    const { entities } = useFetchEntities(entityObjects);
    const { options } = useFetchOptions(optionObjects);

    const entitiesById = useMemo(() => new Map<number, Entity>(entities.map((entity) => [entity.id, entity])), [entities]);
    const optionsById = useMemo(
        () => new Map<string, AssetOption>(options.map((option) => [`${option.entityType}:${option.assetId}:${option.id}`, option])),
        [options]
    );
    const symbolByEntityId = useMemo(() => new Map<number, string>(entities.map((entity) => [entity.id, entity.symbol])), [entities]);

    const strategyColors = useMemo(() => assignStrategyColors(strategies), [strategies]);
    const strategyNames = useMemo(() => new Map(strategies.map((strategy) => [strategy.strategyId, strategy.name])), [strategies]);
    const knownStrategyIds = useMemo(() => new Set(strategies.map((strategy) => strategy.strategyId)), [strategies]);
    const selectedStrategyIds = useMemo(() => parseStrategyScopeParam(strategyParam, knownStrategyIds), [strategyParam, knownStrategyIds]);
    const scope = useMemo(() => scopeFromIds(selectedStrategyIds), [selectedStrategyIds]);
    const selectedStrategies = useMemo(() => strategies.filter((strategy) => scope?.has(strategy.strategyId)), [strategies, scope]);
    const scopeLabel =
        scope === null ? null : selectedStrategies.length === 1 ? selectedStrategies[0].name : `${selectedStrategies.length} strategies`;

    const dayByDate = useMemo(() => new Map(days.map((day) => [day.date, day])), [days]);
    const periodStart = useMemo(() => getPeriodStartDate(period, today), [period, today]);
    const periodDays = useMemo(() => filterDaysByPeriod(days, periodStart), [days, periodStart]);
    const closedTradesInPeriod = useMemo(
        () =>
            trades.filter(
                (trade) =>
                    isClosedTrade(trade) && tradeMatchesScope(trade, scope) && (periodStart === null || trade.exitDate >= periodStart)
            ),
        [trades, scope, periodStart]
    );

    const equityStats = useMemo(() => computeEquityStats(periodDays, basis, scope), [periodDays, basis, scope]);
    const tradeStats = useMemo(() => computeTradeStats(closedTradesInPeriod, today), [closedTradesInPeriod, today]);
    const equityCurve = useMemo(() => buildEquityCurve(periodDays, basis, scope), [periodDays, basis, scope]);
    const strategyRows = useMemo(
        () => buildStrategyRows({ strategies, trades, days: periodDays, periodStart, today }),
        [strategies, trades, periodDays, periodStart, today]
    );
    const scopeItems = useMemo(() => buildStrategyScopeItems(strategyRows), [strategyRows]);
    const ledgerRows = useMemo(
        () => buildLedgerRows({ days: periodDays, basis, scope, hideFlatDays }),
        [periodDays, basis, scope, hideFlatDays]
    );

    const scopedRows = useMemo(
        () => (scope === null ? [] : strategyRows.filter((row) => scope.has(row.strategyId))),
        [strategyRows, scope]
    );
    const scopeOpenCount = useMemo(() => scopedRows.reduce((total, row) => total + row.openCount, 0), [scopedRows]);
    const scopeOpenPnl = useMemo(() => {
        const marked = scopedRows.map((row) => row.openPnl).filter((value): value is number => value !== null);
        return marked.length ? Math.round(marked.reduce((total, value) => total + value, 0) * 100) / 100 : null;
    }, [scopedRows]);

    const selectedDay = selectedDate ? (dayByDate.get(selectedDate) ?? null) : null;
    const dayDetail = useMemo(() => (selectedDay ? getDayDetail(selectedDay, trades, scope) : null), [selectedDay, trades, scope]);

    const expectationsByStrategy = useMemo<Map<number, PortfolioStrategyExpectation> | null>(() => {
        const expectations = expectationsQuery.data?.expectations;
        if (!expectations) return null;
        return new Map(expectations.map((expectation) => [expectation.strategyId, expectation]));
    }, [expectationsQuery.data]);

    const showBasisToggle = useMemo(() => days.some((day) => day.fundingFlow !== 0), [days]);
    const periodDescription = PERFORMANCE_PERIODS.find((entry) => entry.key === period)?.description ?? period;

    const loading = portfolioQuery.isLoading || breakdownQuery.isLoading;

    const error = useMemo(() => {
        if (!portfolioId) return 'Invalid portfolio ID';
        if (portfolioQuery.error) return 'Failed to load portfolio details';
        if (breakdownQuery.error) return 'Failed to load performance data';
        return null;
    }, [portfolioId, portfolioQuery.error, breakdownQuery.error]);

    useEffect(() => {
        if (loading) showSpinner();
        else hideSpinner();

        return () => {
            hideSpinner();
        };
    }, [loading]);

    const handleSelectDay = useCallback(
        (date: string) => {
            updateParams({ day: date, month: getMonthKey(date) });
        },
        [updateParams]
    );

    const handleCloseDay = useCallback(() => updateParams({ day: null }), [updateParams]);

    const handleStrategyIdsChange = useCallback(
        (strategyIds: number[]) => updateParams({ strategy: strategyIds.length ? strategyIds.join(',') : null }),
        [updateParams]
    );

    const handleToggleStrategy = useCallback(
        (strategyId: number) =>
            handleStrategyIdsChange(
                selectedStrategyIds.includes(strategyId)
                    ? selectedStrategyIds.filter((entry) => entry !== strategyId)
                    : [...selectedStrategyIds, strategyId]
            ),
        [handleStrategyIdsChange, selectedStrategyIds]
    );

    const chartLineColor =
        selectedStrategies.length === 1 ? (strategyColors.get(selectedStrategies[0].strategyId) ?? DEFAULT_LINE_COLOR) : DEFAULT_LINE_COLOR;
    const pageTitle = portfolio?.name ? `${portfolio.name} - Performance - Project 314` : 'Performance - Project 314';

    return (
        <UserLayout containerSize="1800px" title={error ? pageTitle.replace(' - Project 314', ' - Error - Project 314') : pageTitle}>
            <div className="container mx-auto mt-3 flex flex-col gap-4 px-4 pb-10">
                <div>
                    <div className="text-sm text-zinc-400">
                        <Link className="hover:text-blue-400" to="/user/portfolios">
                            Portfolios
                        </Link>
                        {portfolio && (
                            <>
                                {' › '}
                                <Link className="hover:text-blue-400" to={`/user/portfolio/${portfolio.id}/`}>
                                    {portfolio.name}
                                </Link>
                            </>
                        )}
                        {' › '}
                        <span className="text-white">Performance</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
                        <h1 className="text-2xl font-semibold text-white">Performance</h1>
                        {breakdown && (
                            <span className="text-xs text-zinc-500">
                                Snapshots through {breakdown.asOf} ET · one calendar day per row, first to last snapshot
                            </span>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-100 p-4 text-red-800" role="alert">
                        {error}
                    </div>
                )}

                {loading ? (
                    <DataLoadingSpinner />
                ) : breakdown && days.length === 0 ? (
                    <EmptyState
                        description="Daily equity appears once the portfolio has recorded its first snapshot. Trades and attribution follow the first fill."
                        title="No performance history yet"
                    />
                ) : breakdown ? (
                    <>
                        <PerformanceControls
                            basis={basis}
                            period={period}
                            scopeItems={scopeItems}
                            selectedStrategyIds={selectedStrategyIds}
                            showBasisToggle={showBasisToggle}
                            onBasisChange={setBasis}
                            onPeriodChange={(nextPeriod) => updateParams({ period: nextPeriod === DEFAULT_PERIOD ? null : nextPeriod })}
                            onStrategyIdsChange={handleStrategyIdsChange}
                        />

                        <Card
                            responsivePadding
                            subtitle={
                                periodDays.length
                                    ? `${formatShortDate(periodDays[0].date)} – ${formatShortDate(today)} · ${
                                          showBasisToggle
                                              ? basis === 'trading'
                                                  ? 'funding removed'
                                                  : 'funding included'
                                              : 'no external funding in range'
                                      }`
                                    : 'No days in this range'
                            }
                            title={scopeLabel ? `${periodDescription} · ${scopeLabel}` : periodDescription}
                        >
                            {periodDays.length ? (
                                <PerformanceKpiTiles
                                    basis={basis}
                                    equityStats={equityStats}
                                    scopeLabel={scopeLabel}
                                    scopeOpenCount={scopeOpenCount}
                                    scopeOpenPnl={scopeOpenPnl}
                                    tradeStats={tradeStats}
                                />
                            ) : (
                                <EmptyState size="sm" title="No snapshots in this period" />
                            )}
                        </Card>

                        <Card
                            responsivePadding
                            subtitle={
                                scopeLabel
                                    ? 'Cumulative realized closes plus mark-to-market changes attributed to the selected strategies'
                                    : 'End-of-day equity with trade closes marked · drawdown from the running peak beneath · click a day for its breakdown'
                            }
                            title={scopeLabel ? `Cumulative P&L · ${scopeLabel}` : 'Equity'}
                        >
                            {equityCurve.length ? (
                                <EquityDrawdownChart
                                    lineColor={chartLineColor}
                                    mode={scopeLabel ? 'strategy' : 'equity'}
                                    points={equityCurve}
                                    onSelectDay={handleSelectDay}
                                />
                            ) : (
                                <EmptyState size="sm" title="Nothing to chart in this period" />
                            )}
                        </Card>

                        <Card responsivePadding title="Calendar">
                            <PnlCalendar
                                basis={basis}
                                dayByDate={dayByDate}
                                days={days}
                                hideWeekends={hideWeekends}
                                mode={calendarMode}
                                monthKey={calendarMonthKey}
                                scope={scope}
                                selectedDate={selectedDate}
                                strategyColors={strategyColors}
                                today={today}
                                trades={trades}
                                onHideWeekendsChange={setHideWeekends}
                                onModeChange={(mode) => updateParams({ calendar: mode === 'equity' ? null : mode })}
                                onMonthChange={(monthKey) => updateParams({ month: monthKey === getMonthKey(today) ? null : monthKey })}
                                onSelectDay={handleSelectDay}
                            />
                        </Card>

                        <Card
                            responsivePadding
                            subtitle="Closed trades in the selected period; open P&L from the latest position marks"
                            title="By strategy"
                        >
                            {strategyRows.length ? (
                                <StrategyAttributionTable
                                    compareToBacktest={compareToBacktest}
                                    expectations={expectationsByStrategy}
                                    expectationsLoading={expectationsQuery.isLoading}
                                    rows={strategyRows}
                                    selectedStrategyIds={selectedStrategyIds}
                                    showDormant={showDormantStrategies}
                                    strategyColors={strategyColors}
                                    onCompareToBacktestChange={setCompareToBacktest}
                                    onShowDormantChange={setShowDormantStrategies}
                                    onToggleStrategy={handleToggleStrategy}
                                />
                            ) : (
                                <EmptyState size="sm" title="No strategies have traded in this portfolio yet" />
                            )}
                        </Card>

                        <Card
                            responsivePadding
                            subtitle="Same statistics vocabulary as Backtest Stats, applied to live results in the selected period"
                            title="Breakdowns"
                        >
                            <PerformanceBreakdownTabs
                                basis={basis}
                                days={periodDays}
                                scope={scope}
                                symbolByEntityId={symbolByEntityId}
                                tab={breakdownTab}
                                today={today}
                                trades={trades}
                                onTabChange={(tab) => updateParams({ tab: tab === 'month' ? null : tab })}
                            />
                        </Card>

                        <DailyLedger
                            exportFileName={`portfolio-${portfolioId}-performance-${period.toLowerCase()}.csv`}
                            hideFlatDays={hideFlatDays}
                            rows={ledgerRows}
                            totalDays={periodDays.length}
                            onHideFlatDaysChange={setHideFlatDays}
                            onSelectDay={handleSelectDay}
                        />
                    </>
                ) : null}
            </div>

            <DayDetailDrawer
                basis={basis}
                detail={dayDetail}
                entitiesById={entitiesById}
                isOpen={dayDetail !== null}
                optionsById={optionsById}
                portfolioId={portfolioId}
                scope={scope}
                strategyColors={strategyColors}
                strategyNames={strategyNames}
                today={today}
                onClose={handleCloseDay}
            />
        </UserLayout>
    );
};

export default PortfolioPerformance;
